import { ImpactLevel } from '@griply/types';
import { BlurHashImage } from 'shared/components/connected/blur-hash-image';
import { typographyToCss } from 'shared/lib/mui';
import styled, { css, DefaultTheme } from 'styled-components';

export enum DataTypes {
  Title = 'title',
  StartDate = 'start-date',
  Deadline = 'deadline',
  Progress = 'progress',
  Tasks = 'tasks',
  Impact = 'impact',
  LifeArea = 'life-area',
  Interactions = 'interactions',
}

const dataTypeStyles = (type: DataTypes, theme: DefaultTheme) => {
  switch (type) {
    case DataTypes.Title:
      return css`
        flex: 1;
      `;
    case DataTypes.Progress:
      return css`
        width: 8.4rem;
        color: ${theme.palette.primary.main};
      `;
    case DataTypes.Impact:
      return css`
        width: 8.8rem;
        display: none;

        @container goal-list (min-width: 60rem) {
          display: flex;
        }
      `;
    case DataTypes.StartDate:
      return css`
        width: 8.8rem;
        display: none;

        @container goal-list (min-width: 80rem) {
          display: flex;
        }
      `;
    case DataTypes.Deadline:
      return css`
        width: 8.8rem;
      `;
    case DataTypes.Tasks:
      return css`
        width: 8.8rem;
        display: none;

        @container goal-list (min-width: 70rem) {
          display: flex;
        }
      `;
    case DataTypes.LifeArea:
      return css`
        width: 16.6rem;
        display: none;

        @container goal-list (min-width: 95rem) {
          display: flex;
        }
      `;
    case DataTypes.Interactions:
      return css`
        width: 4.8rem;
        color: ${theme.palette.grey[500]};
      `;
  }
};

export const Container = styled.div<{
  $isArchived: boolean;
  $isSelected?: boolean;
  $isDropTarget?: boolean;
}>`
  display: flex;
  width: 100%;
  gap: ${({ theme }) => theme.spacing(2)};

  background: ${({ theme }) => theme.palette.background.default};
  border-radius: ${({ theme }) => theme.spacing(2.5)};
  padding: ${({ theme }) => `0 0 0 ${theme.spacing(3)}`};

  ${({ $isArchived }) =>
    $isArchived &&
    css`
      opacity: 0.5;
    `}

  ${({ $isDropTarget, theme }) =>
    $isDropTarget &&
    css`
      background: ${theme.palette.grey[100]} !important;
    `}

  ${({ $isSelected, theme }) =>
    $isSelected &&
    css`
      background: ${theme.palette.primaryExtended.opacityBackgroundBackground};
    `}
`;

export const DataContainer = styled.span<{
  $type: DataTypes;
  $isFrozen: boolean;
  $isClickable?: boolean;
}>`
  ${({ theme }) => typographyToCss(theme.typography.body2)};
  display: flex;
  align-items: center;
  gap: ${({ theme, $type }) =>
    theme.spacing($type === DataTypes.Title ? 2 : 1)};
  height: 4.8rem;

  overflow: hidden;

  ${({ $type, theme }) => dataTypeStyles($type, theme)};

  ${({ $isClickable }) =>
    $isClickable &&
    css`
      cursor: pointer;
    `};

  &:hover ${() => SelectPlaceholder} {
    display: inline;
  }

  ${({ $isFrozen, theme }) =>
    $isFrozen &&
    css`
      color: ${theme.palette.text.secondary};
      pointer-events: none;
    `};
`;

export const IllustrationContainer = styled.span`
  position: relative;
  display: block;
  padding-left: ${({ theme }) => theme.spacing(0.5)};
`;

export const ImageContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 2rem;
  width: 2rem;
  min-width: 2rem;

  border-radius: ${({ theme }) => theme.spacing(1.5)};
  background: ${({ theme }) => theme.palette.grey[100]};
  overflow: hidden;

  font-size: 1.4rem;
  line-height: 62.5%;
  color: ${({ theme }) => theme.palette.grey[700]};
`;

export const Image = styled(BlurHashImage)`
  display: block;
  width: 100%;
  height: 100%;

  object-fit: cover;
`;

export const CompletedContainer = styled.span`
  position: absolute;
  top: 0;
  right: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 1.2rem;
  height: 1.2rem;

  transform: translate(33%, -33%);
  background: ${({ theme }) => theme.palette.success.main};
  border-radius: 50%;

  font-size: 0.8rem;
  color: ${({ theme }) => theme.palette.success.contrastText};
`;

export const ColorDot = styled.span<{ $color: string }>`
  position: absolute;
  bottom: ${({ theme }) => `-${theme.spacing(0.75)}`};
  right: ${({ theme }) => `-${theme.spacing(0.75)}`};

  display: block;
  height: 1rem;
  width: 1rem;

  border: ${({ theme }) =>
    `${theme.spacing(0.5)} solid ${theme.palette.background.default};`};
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

export const Title = styled.span<{ $isCompleted: boolean }>`
  ${({ theme }) => typographyToCss(theme.typography.subtitle2)};

  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ $isCompleted }) =>
    $isCompleted &&
    css`
      text-decoration: line-through;
    `};
`;

export const SelectPlaceholder = styled.span<{ $forceShow?: boolean }>`
  ${({ theme }) => typographyToCss(theme.typography.buttonSmall)};
  display: ${({ $forceShow }) => ($forceShow ? 'inline' : 'none')};
  color: ${({ theme }) => theme.palette.text.secondary};
  padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1.5)}`};
  border-radius: ${({ theme }) => theme.spacing(2.5)};
  border: 1px solid ${({ theme }) => theme.palette.divider};
`;

export const DataButton = styled.span<{
  $isSmallFont?: boolean;
  $background?: string;
  $color?: string;
  $impactLevel?: ImpactLevel;
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};

  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1.5)}`};
  border-radius: ${({ theme }) => theme.spacing(2)};

  &:hover {
    background: ${({ theme }) => theme.palette.grey[100]};
  }

  ${({ $isSmallFont, theme }) =>
    $isSmallFont &&
    css`
      ${typographyToCss(theme.typography.buttonSmall)};
      padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1.5)}`};
    `};

  ${({ $background }) =>
    $background &&
    css`
      background: ${$background};
      border-color: ${$background};
    `};

  ${({ $color }) =>
    $color &&
    css`
      color: ${$color};
    `};

  ${({ $impactLevel, theme }) =>
    $impactLevel &&
    css`
      background: ${theme.palette.goalImpact[$impactLevel].background};
      color: ${theme.palette.goalImpact[$impactLevel].text};
    `};
`;

export const OverflowSpan = styled.span`
  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const IconContainer = styled.span`
  font-size: 1.6rem;
  line-height: 62.5%;
`;
